
/**
 * Compumarts Cart Drawer
 * Handles missing Shopify sections after AJAX cart updates.
 * Uses direct Section Rendering API as a fallback.
 */

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();

      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document
        .getElementById('cart-drawer')
        ?.querySelector('.overlay');
    }

    handleClick(evt) {
      if (!evt.target.closest('.js-close')) return;
      this.close();
    }

    open() {
      this.overlay?.classList.add('is-visible');
      super.open();
      trapFocus(this);

      this.clickHandler =
        this.clickHandler || this.handleClick.bind(this);

      this.keyupHandler =
        this.keyupHandler ||
        (evt => {
          if (evt.key === 'Escape') this.close();
        });

      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay?.addEventListener('click', this.clickHandler);
    }

    close() {
      this.overlay?.classList.remove('is-visible');

      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay?.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define(
    'cart-drawer-disclosure',
    CartDrawerDisclosure
  );
}

/* global SideDrawer, Shopify, theme */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector(
        '.cart-drawer__summary'
      );

      this._renderSequence = 0;
      this._pendingOpen = false;

      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener(
        'dispatch:cart-drawer:refresh',
        this.refreshHandler
      );

      document.removeEventListener(
        'on:bfcache:load-restore',
        this.refreshHandler
      );

      document.removeEventListener(
        'dispatch:cart-drawer:open',
        this.openDrawerViaEventHandler
      );

      document.removeEventListener(
        'dispatch:cart-drawer:close',
        this.closeDrawerViaEventHandler
      );

      if (Shopify.designMode) {
        document.removeEventListener(
          'shopify:section:select',
          this.sectionSelectHandler
        );

        document.removeEventListener(
          'shopify:section:deselect',
          this.sectionDeselectHandler
        );
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener(
          'shopify:payment_button:loaded',
          this.dcbLoadedHandler
        );
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');

      if (!cartIcon) return;

      cartIcon.setAttribute('role', 'button');
      cartIcon.setAttribute('aria-haspopup', 'dialog');

      cartIcon.addEventListener('click', evt => {
        evt.preventDefault();
        this.open(cartIcon);
      });

      cartIcon.addEventListener('keydown', evt => {
        if (evt.key !== ' ') return;

        evt.preventDefault();
        this.open(cartIcon);
      });
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler =
          this.handleSectionSelect.bind(this);

        this.sectionDeselectHandler =
          this.handleSectionDeselect.bind(this);

        document.addEventListener(
          'shopify:section:select',
          this.sectionSelectHandler
        );

        document.addEventListener(
          'shopify:section:deselect',
          this.sectionDeselectHandler
        );
      }

      this.openDrawerViaEventHandler =
        this.handleDrawerOpenViaEvent.bind(this);

      this.closeDrawerViaEventHandler =
        this.close.bind(this, null);

      document.addEventListener(
        'dispatch:cart-drawer:open',
        this.openDrawerViaEventHandler
      );

      document.addEventListener(
        'dispatch:cart-drawer:close',
        this.closeDrawerViaEventHandler
      );

      if (
        this.cartSummary?.classList.contains(
          'cart-drawer--checkout--sticky-true'
        )
      ) {
        this.dcbLoadedHandler =
          this.dcbLoadedHandler ||
          CartDrawer.recalculateCssVarHeights;

        document.addEventListener(
          'shopify:payment_button:loaded',
          this.dcbLoadedHandler
        );
      }

      this.refreshHandler = this.refresh.bind(this);

      document.addEventListener(
        'dispatch:cart-drawer:refresh',
        this.refreshHandler
      );

      document.addEventListener(
        'on:bfcache:load-restore',
        this.refreshHandler
      );
    }

    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) {
        this.open();
      }
    }

    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) {
        this.close();
      }
    }

    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    open(opener, elementToFocus) {
      const quickAddDrawer = document.querySelector(
        'quick-add-drawer[aria-hidden="false"]'
      );

      const overlay = document.querySelector(
        '.js-overlay.is-visible'
      );

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      setTimeout(() => {
        if (!this.isConnected) return;

        super.open(opener, elementToFocus, () => {
          if (
            this.cartSummary?.classList.contains(
              'cart-drawer--checkout--sticky-true'
            )
          ) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (
          this.cartSummary?.classList.contains(
            'cart-drawer--checkout--sticky-true'
          )
        ) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
    }

    /**
     * Return the original Shopify cart section ID.
     * Supports a drawer moved outside its Shopify section.
     */
    getCartSectionId() {
      const parentSection = this.closest('.shopify-section');

      if (parentSection?.id?.startsWith('shopify-section-')) {
        return parentSection.id.replace('shopify-section-', '');
      }

      const candidate = document.querySelector(
        '.shopify-section.cc-cart-drawer[id]'
      );

      if (candidate?.id?.startsWith('shopify-section-')) {
        return candidate.id.replace('shopify-section-', '');
      }

      const sectionFromData =
        this.dataset.sectionId ||
        this.dataset.cartSectionId;

      if (sectionFromData) {
        return sectionFromData.replace(/^shopify-section-/, '');
      }

      // This ID was confirmed by the store's Console tests.
      return 'sections--21232363470997__cart-drawer';
    }

    static sectionsUrl(names) {
      const url = new URL(window.location.href);

      url.searchParams.delete('section_id');
      url.searchParams.set(
        'sections',
        [...new Set(names)].join(',')
      );

      return url.toString();
    }

    static async requestDirectSection(sectionId) {
      const url = new URL(window.location.href);

      url.searchParams.delete('sections');
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(
          `Direct section request failed: ${response.status}`
        );
      }

      const html = await response.text();
      const template = document.createElement('template');

      template.innerHTML = html;

      if (!template.content.querySelector('cart-drawer')) {
        throw new Error(
          'Direct section response does not contain cart-drawer'
        );
      }

      return html;
    }

    static async recoverSections(initialSections, requiredNames) {
      let sections =
        initialSections &&
        typeof initialSections === 'object' &&
        !Array.isArray(initialSections)
          ? { ...initialSections }
          : {};

      const missing = [...new Set(requiredNames)].filter(
        name => typeof sections[name] !== 'string'
      );

      if (!missing.length) return sections;

      try {
        const response = await fetch(
          CartDrawer.sectionsUrl(missing),
          { credentials: 'same-origin', cache: 'no-store' }
        );

        if (!response.ok) {
          throw new Error(
            `Section request failed: ${response.status}`
          );
        }

        const result = await response.json();

        if (
          result &&
          typeof result === 'object' &&
          !Array.isArray(result)
        ) {
          sections = { ...sections, ...result };
        }
      } catch (error) {
        console.warn(
          '[Cart Drawer] Bundled section recovery failed:',
          error
        );
      }

      return sections;
    }

    /**
     * Refresh the cart without opening it automatically.
     */
    async refresh(dontRefreshCartItems = false) {
      try {
        const cartItems = this.querySelector('cart-items');

        if (
          cartItems &&
          !dontRefreshCartItems &&
          typeof cartItems.refresh === 'function'
        ) {
          await cartItems.refresh();
          return;
        }

        const names = this.getSectionsToRender().map(
          section => section.section
        );

        const response = await fetch(
          CartDrawer.sectionsUrl(names),
          { credentials: 'same-origin', cache: 'no-store' }
        );

        if (!response.ok) {
          throw new Error(
            `Cart refresh HTTP ${response.status}`
          );
        }

        const sections = await response.json();

        await this.renderContents({ sections }, false);

        CartDrawer.recalculateCssVarHeights();
      } catch (error) {
        console.error('[Cart Drawer] Refresh error:', error);

        this.dispatchEvent(
          new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error: error.message }
          })
        );
      }
    }

    /**
     * Render the latest Shopify cart HTML.
     * The drawer opens after the content has been updated.
     */
    async renderContents(data, openDrawer = true) {
      const sequence = ++this._renderSequence;

      if (!this.scriptsLoaded) {
        if (
          theme.scripts.cartItems &&
          !document.querySelector(
            `script[src="${theme.scripts.cartItems}"]`
          )
        ) {
          await loadScript(theme.scripts.cartItems);
        }

        if (
          this.dataset.shippingCalculator === 'true' &&
          theme.scripts.shippingCalculator &&
          !document.querySelector(
            `script[src="${theme.scripts.shippingCalculator}"]`
          )
        ) {
          if (theme.scripts.countryProvinceSelector) {
            await loadScript(
              theme.scripts.countryProvinceSelector
            );
          }

          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      const sectionsToRender = this.getSectionsToRender();

      const names = sectionsToRender.map(
        section => section.section
      );

      let sections = await CartDrawer.recoverSections(
        data?.sections,
        names
      );

      const drawerSection = sectionsToRender.find(
        section => section.id === 'cart-drawer'
      );

      if (!drawerSection) {
        console.error(
          '[Cart Drawer] Unable to determine cart section ID.'
        );
        return;
      }

      // Direct fallback confirmed to work on this Shopify store.
      if (
        typeof sections[drawerSection.section] !== 'string'
      ) {
        try {
          const html = await CartDrawer.requestDirectSection(
            drawerSection.section
          );

          sections[drawerSection.section] = html;
        } catch (error) {
          console.error(
            '[Cart Drawer] Direct section recovery failed:',
            error
          );
        }
      }

      if (sequence !== this._renderSequence) return;

      let drawerUpdated = false;

      sectionsToRender.forEach(section => {
        const target = document.getElementById(section.id);
        const html = sections[section.section];

        if (!target || typeof html !== 'string') return;

        const template = document.createElement('template');
        template.innerHTML = html;

        const source = template.content.querySelector(
          section.selector
        );

        if (!source) {
          console.warn(
            '[Cart Drawer] Missing selector:',
            section.selector
          );
          return;
        }

        target.innerHTML = source.innerHTML;

        if (section.id === 'cart-drawer') {
          drawerUpdated = true;
        }
      });

      this.cartSummary = this.querySelector(
        '.cart-drawer__summary'
      );

      CartDrawer.recalculateCssVarHeights();

      // Do not display an outdated empty-cart state.
      if (!drawerUpdated) {
        console.error(
          '[Cart Drawer] Fresh cart HTML was unavailable.'
        );
        return;
      }

      if (
        openDrawer &&
        this.getAttribute('open') === null
      ) {
        setTimeout(() => {
          if (
            this.isConnected &&
            sequence === this._renderSequence
          ) {
            this.open();
          }
        }, 0);
      }
    }

    getSectionsToRender() {
      const sectionId = this.getCartSectionId();

      return [
        {
          id: 'cart-drawer',
          section: sectionId,
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(
          new CustomEvent('on:css-var-height:update')
        );
      });
    }

    static getElementHTML(html, selector) {
      if (typeof html !== 'string') return '';

      const template = document.createElement('template');
      template.innerHTML = html;

      return template.content.querySelector(
        selector
      )?.innerHTML || '';
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
